/**
 * The little pictures in a builder's palettes.
 *
 * Every one of them is drawn by the game's own painter rather than by an icon: a swatch that only
 * approximates what the brush will do is a swatch that teaches the wrong thing, and this is the same
 * rule the canvas itself is built on.
 *
 * Shared, because both editors have palettes and neither should own the drawing of a tile.
 */

import { useEffect, useRef } from 'react';

import type { CharacterId, PropId } from '../../../core/content';
import { TERRAIN_LABELS, copy } from '../../../core/copy';
import { TERRAIN_VALUES, Terrain } from '../../../core/terrain';
import { characterPose } from '../../art/characters';
import { biomeFor } from '../../art/palette';
import { bake, propArt, spriteColors } from '../../art/sprites';
import { renderGroundLayer } from '../../game/tiles';
import { TILE } from '../../game/world';
import { previewWorld } from './paint';

export function TerrainPalette({
  biome,
  value,
  onChange,
  values = TERRAIN_VALUES,
  title = copy.teren.paletteTerrain,
}: {
  biome: string | undefined;
  value: Terrain;
  onChange: (next: Terrain) => void;
  /**
   * Which swatches to offer.
   *
   * A parameter rather than the whole enum, because the two editors want different vocabularies: an
   * admin needs every one of them including `Fog`, which is the edge of the world, and a member
   * arranging their own island has no business painting the edge of the world.
   */
  values?: readonly Terrain[];
  title?: string;
}) {
  return (
    <div className="wb-panel">
      <b className="lbl">{title}</b>
      <div className="wb-swatches">
        {values.map((terrain) => (
          <button
            key={terrain}
            type="button"
            className={`wb-swatch ${value === terrain ? 'on' : ''}`.trim()}
            aria-pressed={value === terrain}
            onClick={() => onChange(terrain)}
          >
            <TerrainSwatch terrain={terrain} biome={biome} />
            <span className="tiny">{TERRAIN_LABELS[terrain]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * One tile, painted by the game's own painter.
 *
 * Rendered as the middle of a 3×3 field of itself rather than alone, because half the painters look
 * at their neighbours: a lone bridge tile has no direction, a lone fence has no rails and a lone
 * path is all edge. The swatch has to show what a run of it looks like, which is what is actually
 * about to be drawn.
 */
export function TerrainSwatch({ terrain, biome }: { terrain: Terrain; biome: string | undefined }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const tiles = new Uint8Array(9).fill(terrain);
    const rendered = renderGroundLayer(previewWorld(tiles, 3, 3), () => biomeFor(biome));
    canvas.width = TILE;
    canvas.height = TILE;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, TILE, TILE);
    ctx.drawImage(rendered, TILE, TILE, TILE, TILE, 0, 0, TILE, TILE);
  }, [terrain, biome]);

  return <canvas ref={ref} className="wb-swatchart" aria-hidden="true" />;
}

export function CharacterChip({ art }: { art: CharacterId }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const pose = characterPose(art, 'dole');
    if (!pose) return;
    const baked = bake(pose.key, pose.rows, pose.colors, 2, pose.flip);
    canvas.width = baked.width;
    canvas.height = baked.height;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(baked.canvas, 0, 0);
  }, [art]);
  return <canvas ref={ref} aria-hidden="true" />;
}

export function PropChip({ kind }: { kind: PropId }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const art = propArt(kind);
    if (!art) return;
    const baked = bake(`wb:chip:${kind}`, art.frames[0]!, spriteColors, 2);
    canvas.width = baked.width;
    canvas.height = baked.height;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(baked.canvas, 0, 0);
  }, [kind]);
  return <canvas ref={ref} aria-hidden="true" />;
}
